import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck,
  FileSearch,
  UserCheck,
  BadgeCheck,
  ArrowRight,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const steps = [
  {
    n: "01",
    icon: FileSearch,
    title: "You Submit the Application",
    timeframe: "Takes 2 minutes",
    text:
      "Fill in the form on our Get Verified page — your domain, a brief description of what your website does, and a few questions about your policies. We ask whether you have a privacy policy, terms of service, and a refund policy, and whether your site is currently live. That's all we need to start.",
  },
  {
    n: "02",
    icon: ShieldCheck,
    title: "Automatic Safety Checks Run",
    timeframe: "Instant",
    text:
      "As soon as you submit, our system runs the same checks your visitors would see in the free checker: we scan your domain against public threat intelligence databases, verify your SSL certificate is active and valid, and check how long the domain has been registered. If your domain is already flagged in malware or phishing databases, we catch that immediately. Most legitimate sites pass this step without any issues.",
  },
  {
    n: "03",
    icon: UserCheck,
    title: "One-Time Eligibility Check",
    timeframe: "1–2 Business Days",
    text:
      "Before your automated monitoring subscription begins, someone on our team confirms your site meets our published criteria: a working contact method, readable policies, and honest, non-misleading content. This is a standardized, one-time check against the same checklist for every applicant — we're not looking for perfection, just confirming you're running a legitimate operation. If something looks off, we'll email you explaining what we found.",
  },
  {
    n: "04",
    icon: BadgeCheck,
    title: "Badge Goes Live — Monitoring Continues",
    timeframe: "Ongoing",
    text:
      "If your site passes review, your domain status changes to Verified in our checker. Anyone who checks your domain from that point forward sees the Verified result instead of New/Unknown. You'll also receive an embed code — a small HTML snippet — that you can place anywhere on your site. Visitors can click it to see your verification details. While your subscription is active, we continue checking your domain against security databases. If your site is ever flagged for malware or phishing after being verified, your badge is suspended immediately to protect visitors.",
  },
];

const criteria = [
  {
    item: "No malware or phishing flags",
    what: "Your domain must be clean in public threat databases at time of review.",
    why: "A site carrying known threats cannot be verified regardless of anything else.",
  },
  {
    item: "Valid SSL certificate",
    what: "Your site must be accessible over HTTPS with a valid, active certificate.",
    why: "No SSL means visitor data isn't encrypted in transit — that's a basic safety failure.",
  },
  {
    item: "Privacy policy",
    what: "A privacy policy that's actually linked and readable — not buried or placeholder text.",
    why: "Required by law in most countries and a baseline for any legitimate website.",
  },
  {
    item: "Honest, readable content",
    what: "Your site must do what it says it does. No misleading claims, fake urgency, or deceptive design.",
    why: "The badge tells visitors your site was reviewed. That review has to mean something.",
  },
  {
    item: "Working contact method",
    what: "At minimum, a real email address visitors can use to reach you.",
    why: "A site with no way to contact the owner gives visitors no recourse if something goes wrong.",
  },
];

const faqs = [
  {
    q: "How long does the review actually take?",
    a: "The automatic checks are instant. The one-time eligibility check is done within 1–2 business days. We'll email you the outcome either way — approved or not approved, with a reason.",
  },
  {
    q: "What if my site doesn't pass?",
    a: "We'll email you explaining exactly what we found and what you'd need to change. You can fix the issues and resubmit — no extra charge for a re-review.",
  },
  {
    q: "Do you check my site again after I'm approved?",
    a: "Our system continuously checks verified domains against security databases. If your domain gets flagged for malware or phishing after being verified, your badge is suspended immediately. We also reserve the right to manually re-review any verified site if something is reported.",
  },
  {
    q: "My site is new — will that count against me?",
    a: "Not on its own. A new domain will show as New/Unknown in our free checker, which is exactly why the badge exists. What matters at review is whether your site is honest and legitimate — not how old it is.",
  },
  {
    q: "Can I use the badge on multiple domains?",
    a: "Each domain needs its own subscription at $10/month. The badge embed code is locked to the specific domain that was verified — it won't show as verified on a different domain.",
  },
  {
    q: "What happens if I cancel?",
    a: "Your badge deactivates at the end of your billing period. Your domain returns to showing its normal checker result based on automatic signals. No partial refunds, but no lock-in either.",
  },
];

export default function VerificationProcess() {
  return (
    <Layout>
      <Helmet>
        <title>How Website Verification Works — Transparency Badge</title>
        <meta
          name="description"
          content="Here's exactly how the Transparency Badge verification process works — what we check, who reviews it, and what it means for your visitors."
        />
        <link rel="canonical" href="https://transparencybadge.com/verification-process" />
        <meta property="og:title" content="How Website Verification Works — Transparency Badge" />
        <meta property="og:description" content="What we actually check, who reviews your site, and what the badge means for your visitors." />
        <meta property="og:url" content="https://transparencybadge.com/verification-process" />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 gradient-hero opacity-[0.97]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container relative py-20 md:py-28">
          <div className="max-w-3xl mx-auto text-center text-primary-foreground">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-white/10 backdrop-blur border border-white/20 mb-6">
              <ShieldCheck className="h-3.5 w-3.5" />
              How Verification Works
            </span>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5 text-balance">
              What Actually Happens When You Apply
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/85 text-balance mb-9">
              No black boxes. Here's the exact process — what we check, in what order,
              and what each step means for you and your visitors.
            </p>
            <Button asChild size="lg" className="btn-orange group">
              <Link to="/get-verified">
                Apply for Verification
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* THE HONEST NOTE */}
      <section className="py-10 border-b bg-accent/30">
        <div className="container max-w-3xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <AlertCircle className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground mb-1">What verification is — and what it isn't</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The Transparency Badge is not a legal certification and we're not a government body.
                It's a standardized, subscription-based monitoring service: every applicant is checked against
                the same published criteria, your domain is continuously re-scanned against threat databases
                for as long as your subscription is active, and your badge is suspended automatically if a
                problem is ever detected. The one-time eligibility check at onboarding confirms your site is
                a legitimate, honest operation before monitoring begins. It's a meaningful trust signal —
                but not a guarantee that a business is perfect or that it will never have issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-16 md:py-24">
        <div className="container">
          <SectionHeader
            badge="The Process"
            title="Four Steps, Start to Finish"
            description="Every application goes through the same steps in the same order. Here's what happens."
          />

          <div className="relative max-w-4xl mx-auto mt-14">
            <div className="absolute left-6 md:left-10 top-2 bottom-2 w-px bg-gradient-to-b from-primary/40 via-border to-transparent hidden sm:block" />

            <div className="space-y-6">
              {steps.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.n} className="relative sm:pl-24">
                    <div className="hidden sm:flex absolute left-0 top-6 h-12 w-12 md:h-20 md:w-20 items-center justify-center">
                      <div className="relative h-12 w-12 rounded-xl gradient-hero shadow-badge flex items-center justify-center text-primary-foreground">
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                    <Card className="group transition-all hover:shadow-elevated hover:-translate-y-0.5 border-border/70">
                      <CardContent className="p-6 md:p-7">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                          <div className="flex items-center gap-3">
                            <div className="sm:hidden h-10 w-10 rounded-lg gradient-hero flex items-center justify-center text-primary-foreground">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-xs font-mono font-semibold text-primary/70 mb-0.5">
                                STEP {s.n}
                              </div>
                              <h3 className="text-xl font-semibold text-foreground">{s.title}</h3>
                            </div>
                          </div>
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-accent text-accent-foreground">
                            <Clock className="h-3 w-3" />
                            {s.timeframe}
                          </span>
                        </div>
                        <p className="text-muted-foreground leading-relaxed">{s.text}</p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CRITERIA TABLE */}
      <section className="py-16 md:py-24 bg-muted/40 border-y">
        <div className="container">
          <SectionHeader
            badge="What We Check"
            title="The Five Things Every Site Must Pass"
            description="These aren't arbitrary boxes. Each one exists because it directly affects whether a visitor can trust your site."
          />

          <div className="max-w-5xl mx-auto mt-12 rounded-xl border bg-card shadow-soft overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className="font-semibold text-foreground py-4">Requirement</TableHead>
                  <TableHead className="font-semibold text-foreground">What We Look At</TableHead>
                  <TableHead className="font-semibold text-foreground">Why It Matters</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {criteria.map((c) => (
                  <TableRow key={c.item} className="transition-colors">
                    <TableCell className="font-semibold text-foreground py-4 align-top">{c.item}</TableCell>
                    <TableCell className="text-muted-foreground align-top">{c.what}</TableCell>
                    <TableCell className="text-muted-foreground align-top">{c.why}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute -inset-1 gradient-hero rounded-2xl blur opacity-20" />
            <div className="relative rounded-2xl border bg-card p-8 md:p-12 shadow-elevated text-center space-y-4">
              <div className="h-14 w-14 rounded-xl gradient-hero flex items-center justify-center text-primary-foreground shadow-badge mx-auto">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">The badge can't be bought — only earned</h3>
              <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto">
                Paying for the subscription covers the ongoing monitoring and keeps your verification active.
                It doesn't buy approval. Sites that don't meet the criteria don't get the badge —
                full stop. That's what makes the badge worth something to the visitors who see it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-muted/40 border-t">
        <div className="container">
          <SectionHeader
            badge="Questions"
            title="Things People Ask Before Applying"
            description="Straightforward answers to the questions we hear most."
          />
          <div className="max-w-3xl mx-auto mt-12">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border rounded-xl bg-card px-5 shadow-soft data-[state=open]:shadow-elevated transition-shadow"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-12 text-center">
              <Button asChild size="lg" className="btn-orange group">
                <Link to="/get-verified">
                  Apply — First Month Free
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <p className="text-xs text-muted-foreground mt-3">No credit card required to apply.</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
